/**
 * PlatformSettings.js — SuperAdmin Platform Configuration
 *
 * Full Tabs layout (6 tabs):
 *   Tab 1: General       — Payment Gateway + App Settings
 *   Tab 2: Email         — EmailSettingsPanel
 *   Tab 3: Integrations  — WhatsAppBiometricPanel
 *   Tab 4: File Storage  — CloudinarySettingsPanel
 *   Tab 5: AI Assistant  — AISettingsPanel  ← NEW
 *   Tab 6: Tax Slabs     — ProfessionalTaxSlabs (PT slab editor)
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Form, Button, Badge,
    Spinner, Alert, Tabs, Tab,
} from 'react-bootstrap';
import { CreditCard, Save, Settings, Shield, Mail, Zap, Database, Bot } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import EmailSettingsPanel      from './EmailSettingsPanel';
import WhatsAppBiometricPanel  from './WhatsAppBiometricPanel';
import CloudinarySettingsPanel from './CloudinarySettingsPanel';
import AISettingsPanel         from './AISettingsPanel';
import ProfessionalTaxSlabs    from '../settings/ProfessionalTaxSlabs';
const PlatformSettings = () => {
    const [loading,      setLoading]      = useState(true);
    const [saving,       setSaving]       = useState(false);
    const [gateway,      setGateway]      = useState({
        active_gateway:   'razorpay',
        razorpay_enabled: true,
        stripe_enabled:   true,
    });
    const [appName,      setAppName]      = useState('PeoplesDock');
    const [trialDays,    setTrialDays]    = useState(14);
    const [supportEmail, setSupportEmail] = useState('');
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axiosInstance.get('/superadmin/settings');
                const s   = res.data.data || {};
                setGateway({
                    active_gateway:   s.payment_gateway_active?.value || 'razorpay',
                    razorpay_enabled: s.razorpay_enabled?.value === '1',
                    stripe_enabled:   s.stripe_enabled?.value   === '1',
                });
                setAppName(s.app_name?.value || 'PeoplesDock');
                setTrialDays(parseInt(s.trial_days?.value || '14'));
                setSupportEmail(s.support_email?.value || '');
            } catch { toast.error('Failed to load settings'); }
            finally  { setLoading(false); }
        };
        fetchSettings();
    }, []);
    const handleGatewaySave = async () => {
        if (!gateway.razorpay_enabled && !gateway.stripe_enabled) {
            toast.warn('At least one payment gateway must be enabled.');
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.put('/superadmin/settings/payment-gateway', gateway);
            toast.success('Payment gateway settings saved!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save');
        } finally { setSaving(false); }
    };
    const handleAppSettingsSave = async () => {
        setSaving(true);
        try {
            await Promise.all([
                axiosInstance.put('/superadmin/settings/app_name',     { value: appName }),
                axiosInstance.put('/superadmin/settings/trial_days',   { value: String(trialDays) }),
                axiosInstance.put('/superadmin/settings/support_email',{ value: supportEmail }),
            ]);
            toast.success('Application settings saved!');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };
    if (loading) return (
        <div className="text-center py-5">
            <Spinner animation="border" variant="primary"/>
        </div>
    );
    return (
        <div className="p-2 p-md-4">
            {/* Page header */}
            <div className="mb-4">
                <h4 className="fw-bold mb-1">
                    <Settings size={22} className="me-2 text-primary"/>Platform Settings
                </h4>
                <p className="text-muted small mb-0">
                    Configure payment gateways, email, integrations, file storage,
                    AI Assistant provider, and professional tax slabs.
                </p>
            </div>
            {/* ── Tabs ── */}
            <Tabs defaultActiveKey="general" className="mb-4">
                {/* ══ Tab 1: General ══════════════════════════════════════ */}
                <Tab
                    eventKey="general"
                    title={<span><Settings size={14} className="me-1"/>General</span>}>
                    <Row className="g-4 mt-1">
                        {/* Payment Gateway */}
                        <Col xs={12} lg={7}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0 py-3">
                                    <h6 className="fw-bold mb-0">
                                        <CreditCard size={18} className="me-2 text-primary"/>
                                        Payment Gateway Configuration
                                    </h6>
                                    <p className="text-muted small mb-0 mt-1">
                                        Control which payment gateways appear on the signup page.
                                    </p>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    {/* Razorpay */}
                                    <div
                                        className={`p-3 rounded-3 border mb-3 ${gateway.razorpay_enabled ? 'border-primary' : 'border-0 bg-light'}`}
                                        style={{ background: gateway.razorpay_enabled ? '#eef2ff' : undefined }}>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="d-flex align-items-center gap-3">
                                                <div style={{
                                                    width:44, height:44, background:'#3395FF',
                                                    borderRadius:10, display:'flex',
                                                    alignItems:'center', justifyContent:'center',
                                                }}>
                                                    <span style={{ color:'white', fontWeight:800, fontSize:13 }}>RZP</span>
                                                </div>
                                                <div>
                                                    <div className="fw-bold">Razorpay</div>
                                                    <div className="text-muted small">
                                                        UPI · Cards · NetBanking · Wallets · India-first
                                                    </div>
                                                </div>
                                            </div>
                                            <Form.Check
                                                type="switch"
                                                id="razorpay-toggle"
                                                checked={gateway.razorpay_enabled}
                                                onChange={e => setGateway(p => ({
                                                    ...p,
                                                    razorpay_enabled: e.target.checked,
                                                    active_gateway: e.target.checked
                                                        ? 'razorpay'
                                                        : (p.stripe_enabled ? 'stripe' : 'none'),
                                                }))}/>
                                        </div>
                                        {gateway.razorpay_enabled && (
                                            <div className="mt-3">
                                                <Form.Check
                                                    type="radio"
                                                    id="gw-razorpay"
                                                    label={
                                                        <span className="small fw-medium">
                                                            Set as default gateway{' '}
                                                            <Badge bg="primary" className="ms-1">Primary</Badge>
                                                        </span>
                                                    }
                                                    checked={gateway.active_gateway === 'razorpay'}
                                                    onChange={() => setGateway(p => ({ ...p, active_gateway: 'razorpay' }))}/>
                                            </div>
                                        )}
                                    </div>
                                    {/* Stripe */}
                                    <div
                                        className={`p-3 rounded-3 border mb-4 ${gateway.stripe_enabled ? 'border-primary' : 'border-0 bg-light'}`}
                                        style={{ background: gateway.stripe_enabled ? '#eef2ff' : undefined }}>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="d-flex align-items-center gap-3">
                                                <div style={{
                                                    width:44, height:44, background:'#635BFF',
                                                    borderRadius:10, display:'flex',
                                                    alignItems:'center', justifyContent:'center',
                                                }}>
                                                    <span style={{ color:'white', fontWeight:800, fontSize:16 }}>S</span>
                                                </div>
                                                <div>
                                                    <div className="fw-bold">Stripe</div>
                                                    <div className="text-muted small">
                                                        International Cards · Global payments
                                                    </div>
                                                </div>
                                            </div>
                                            <Form.Check
                                                type="switch"
                                                id="stripe-toggle"
                                                checked={gateway.stripe_enabled}
                                                onChange={e => setGateway(p => ({
                                                    ...p,
                                                    stripe_enabled: e.target.checked,
                                                    active_gateway: e.target.checked
                                                        ? 'stripe'
                                                        : (p.razorpay_enabled ? 'razorpay' : 'none'),
                                                }))}/>
                                        </div>
                                        {gateway.stripe_enabled && (
                                            <div className="mt-3">
                                                <Form.Check
                                                    type="radio"
                                                    id="gw-stripe"
                                                    label={
                                                        <span className="small fw-medium">
                                                            Set as default gateway{' '}
                                                            <Badge bg="primary" className="ms-1">Primary</Badge>
                                                        </span>
                                                    }
                                                    checked={gateway.active_gateway === 'stripe'}
                                                    onChange={() => setGateway(p => ({ ...p, active_gateway: 'stripe' }))}/>
                                            </div>
                                        )}
                                    </div>
                                    {!gateway.razorpay_enabled && !gateway.stripe_enabled && (
                                        <Alert variant="warning" className="small mb-3">
                                            <Shield size={14} className="me-1"/>
                                            Warning: No gateway enabled. Users cannot complete signup.
                                        </Alert>
                                    )}
                                    <div className="p-3 bg-light rounded-3 mb-4">
                                        <p className="small fw-bold mb-1">Current Configuration</p>
                                        <p className="small text-muted mb-0">
                                            Signup page will show:{' '}
                                            {[
                                                gateway.razorpay_enabled && 'Razorpay',
                                                gateway.stripe_enabled   && 'Stripe',
                                            ].filter(Boolean).join(' + ') || 'Nothing (disabled)'}
                                            {' '}· Default:{' '}
                                            <strong>{gateway.active_gateway || 'None'}</strong>
                                        </p>
                                    </div>
                                    <Button
                                        variant="primary"
                                        className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                                        onClick={handleGatewaySave}
                                        disabled={saving}>
                                        {saving
                                            ? <Spinner size="sm"/>
                                            : <><Save size={16}/>Save Gateway Settings</>}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                        {/* App Settings */}
                        <Col xs={12} lg={5}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0 py-3">
                                    <h6 className="fw-bold mb-0">
                                        <Settings size={18} className="me-2 text-primary"/>
                                        Application Settings
                                    </h6>
                                    <p className="text-muted small mb-0 mt-1">
                                        Global application configuration.
                                    </p>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">Application Name</Form.Label>
                                        <Form.Control
                                            value={appName}
                                            onChange={e => setAppName(e.target.value)}
                                            placeholder="PeoplesDock"/>
                                        <Form.Text className="text-muted">
                                            Shown on login, signup and email templates.
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">
                                            Free Trial Duration (days)
                                        </Form.Label>
                                        <Form.Control
                                            type="number" min="0" max="90"
                                            value={trialDays}
                                            onChange={e => setTrialDays(parseInt(e.target.value))}/>
                                        <Form.Text className="text-muted">
                                            New companies get this many days for free.
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="small fw-bold">Support Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={supportEmail}
                                            onChange={e => setSupportEmail(e.target.value)}
                                            placeholder="support@peoplesdock.com"/>
                                        <Form.Text className="text-muted">
                                            Shown on login and signup pages.
                                        </Form.Text>
                                    </Form.Group>
                                    <Button
                                        variant="primary"
                                        className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                                        onClick={handleAppSettingsSave}
                                        disabled={saving}>
                                        {saving
                                            ? <Spinner size="sm"/>
                                            : <><Save size={16}/>Save App Settings</>}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>
                {/* ══ Tab 2: Email ════════════════════════════════════════ */}
                <Tab
                    eventKey="email"
                    title={<span><Mail size={14} className="me-1"/>Email</span>}>
                    <div className="mt-1">
                        <EmailSettingsPanel/>
                    </div>
                </Tab>
                {/* ══ Tab 3: Integrations ═════════════════════════════════ */}
                <Tab
                    eventKey="integrations"
                    title={<span><Zap size={14} className="me-1"/>Integrations</span>}>
                    <div className="mt-1">
                        <WhatsAppBiometricPanel/>
                    </div>
                </Tab>
                {/* ══ Tab 4: File Storage ═════════════════════════════════ */}
                <Tab
                    eventKey="storage"
                    title={<span><Database size={14} className="me-1"/>File Storage</span>}>
                    <div className="mt-1">
                        <CloudinarySettingsPanel/>
                    </div>
                </Tab>
                {/* ══ Tab 5: AI Assistant ═════════════════════════════════ NEW */}
                <Tab
                    eventKey="ai"
                    title={<span><Bot size={14} className="me-1"/>AI Assistant</span>}>
                    <div className="mt-1">
                        <AISettingsPanel/>
                    </div>
                </Tab>
                {/* ══ Tab 6: Professional Tax Slabs ══════════════════════ */}
                <Tab
                    eventKey="pt-slabs"
                    title={<span>🏛️ PT Slabs</span>}>
                    <div className="mt-1">
                        <ProfessionalTaxSlabs/>
                    </div>
                </Tab>
            </Tabs>
        </div>
    );
};
export default PlatformSettings;
